import { FC, useRef } from 'react';
import {
  Avatar,
  Chip,
  ListItemIcon,
  MenuItem,
  Popover,
  styled,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { ExitToApp } from '@mui/icons-material';

import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';
import { useLogout } from '../services/flowManager';
import { useGetMerchantConfigurationOptions } from '../services/configManager';
import { useSetCurrentModal } from '../services/modalManager';
import { useGetUserProfile } from '../services/userManager';

const UserMenuChipInverted = styled(Chip)(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  borderLeft: `8px solid ${theme.palette.secondary.light}`,
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: 16,
  paddingRight: 8,
}));

const StyledListItemIcon = styled(ListItemIcon)({
  color: 'inherit',
  paddingLeft: 40,
});

const StyledExitToAppIcon = styled(ExitToApp)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
}));

type CustomMenuItemProps = {
  text: string;
  action: () => void;
  icon?: JSX.Element;
};

const CustomMenuItem: FC<CustomMenuItemProps> = ({ action, text, icon }) => {
  return (
    <StyledMenuItem onClick={action}>
      {text}
      {icon && <StyledListItemIcon>{icon}</StyledListItemIcon>}
    </StyledMenuItem>
  );
};

const UserMenu: FC = () => {
  const menu = usePopupState({
    variant: 'popover',
    popupId: 'select-language',
  });
  const chip = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const { connectionType } = useGetUserProfile() ?? {};
  const logout = useLogout();
  const updateCurrentModal = useSetCurrentModal();
  const openPasswordModal = () => updateCurrentModal('changePassword');

  const consumerSelfApply =
    useGetMerchantConfigurationOptions().consumerSelfApply;

  const onAuthenticatedClick = () => {
    menu.setOpen(true, chip.current as HTMLDivElement);
  };

  const handleLogout = () => {
    logout();
    menu.setOpen(false);
  };

  const changePassword = () => {
    openPasswordModal();
    menu.setOpen(false);
  };

  return (
    <>
      <UserMenuChipInverted
        data-testid="user-menu"
        onClick={onAuthenticatedClick}
        ref={chip}
        avatar={<Avatar src={user?.picture} />}
        clickable={false}
      />
      <Popover {...bindPopover(menu)}>
        {isAuthenticated ? (
          <>
            {connectionType === 'UsernamePassword' && (
              <CustomMenuItem action={changePassword} text="Change Password" />
            )}
            <CustomMenuItem
              action={handleLogout}
              text={consumerSelfApply ? 'Switch to Customer Mode' : 'Logout'}
              icon={<StyledExitToAppIcon />}
            />
          </>
        ) : (
          <CustomMenuItem
            action={loginWithRedirect}
            text="Login"
            icon={<StyledExitToAppIcon />}
          />
        )}
      </Popover>
    </>
  );
};

export default UserMenu;
